import { ref, nextTick, type Ref } from 'vue'
import { N8N_UPLOAD_URL } from '@/api/n8n'
import { postMessage } from '@/services/messagesService'
import { assignAgentViaSystem, setConversationLabels, toggleConversationStatusViaSystem } from '@/services/conversationActionsService'
import { logFirstAgentMessage } from '@/services/callConvService'
import { fileToBase64 } from '@/composables/useMediaHelpers'
import type { ChatMessage } from '@/types/chat'

function isForaDeHorarioLabel(lbl: string): boolean {
  return lbl.toLowerCase().replace(/[\s_]+/g, '-') === 'atendimento-fora-de-horario'
}

export interface UseMessageSenderOptions {
  getConvId: () => number | null
  getToken: () => string
  getContactPhone: () => string | null | undefined
  /** Assinatura do usuário já formatada (de useMessageSearch/getSig()). */
  getSig: () => string
  draft: Ref<string>
  files: Ref<File[]>
  /** true = enviar como nota interna (private, não entregue ao cliente). Resetado após o envio, como draft/files. */
  isPrivate?: Ref<boolean>
  fileInputEl: Ref<HTMLInputElement | null>
  taEl: Ref<HTMLTextAreaElement | null>
  scrollBottom: () => void
  /** Aplica a mensagem criada ao estado local de cada componente (cache por conversa vs lista única). */
  onMessageCreated: (msg: ChatMessage, convId: number) => void
  /** Chamado ao final de um envio (ex: limpar badge de não lida). Opcional — só o ConvDrawer usa. */
  onSent?: (convId: number) => void
  /** Id do agente atualmente atribuído à conversa (null/undefined = sem agente). */
  getAssigneeId: () => number | null | undefined
  /** Id do agente logado — quem recebe a auto-atribuição quando a conversa está sem responsável. */
  getMyAgentId: () => number
  /** Chamado quando o auto-atribuir acontece, pro chamador refletir no estado local (cabeçalho, lista etc.). */
  onAutoAssigned?: (convId: number, agentId: number) => void
  /** Labels atualmente aplicadas à conversa (para remover "atendimento fora de horario" quando o agente responde). */
  getLabels?: () => string[]
  /** Chamado quando a etiqueta "atendimento fora de horario" é removida automaticamente, pro chamador refletir no estado local. */
  onLabelsChanged?: (convId: number, labels: string[]) => void
  /** Status atual da conversa (para reabrir automaticamente quando pendente e o agente responde). */
  getStatus?: () => string | null | undefined
  /** Chamado quando a conversa é reaberta automaticamente, pro chamador refletir no estado local. */
  onStatusChanged?: (convId: number, status: string) => void
}

/** Envia mensagem de texto (com assinatura) + anexos (via webhook de upload do n8n). */
export function useMessageSender(options: UseMessageSenderOptions) {
  const sending = ref(false)

  async function sendMsg() {
    const convId = options.getConvId()
    if (sending.value || convId == null) return
    sending.value = true

    const filesToSend = [...options.files.value]
    const textContent = options.draft.value.trim()
    const sig = options.getSig()
    const textWithSig = textContent ? (sig ? `${textContent}\n\n${sig}` : textContent) : textContent
    const token = options.getToken()
    const isPrivate = options.isPrivate?.value ?? false

    options.draft.value = ''
    options.files.value = []
    if (options.isPrivate) options.isPrivate.value = false
    if (options.fileInputEl.value) options.fileInputEl.value.value = ''
    if (options.taEl.value) options.taEl.value.style.height = 'auto'

    try {
      if (filesToSend.length) {
        if (textWithSig) {
          const msg = await postMessage(convId, token, textWithSig, isPrivate)
          if (msg) options.onMessageCreated(msg, convId)
        }
        for (const file of filesToSend) {
          try {
            const base64 = await fileToBase64(file)
            await fetch(N8N_UPLOAD_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                base64,
                filename: file.name,
                mimeType: file.type || 'application/octet-stream',
                conversationId: String(convId),
                contactPhone: options.getContactPhone() ?? '',
                apiToken: token,
              }),
            })
          } catch { /* silencioso — n8n responde pelo próprio fluxo */ }
        }
      } else {
        const msg = await postMessage(convId, token, textWithSig, isPrivate)
        if (msg) options.onMessageCreated(msg, convId)
      }

      // Registra a 1ª mensagem do agente pro cliente na conversa (migração
      // v36) — nota interna não conta (decisão do plano). Fire-and-forget:
      // não pode atrasar o envio, e é idempotente do lado do banco (só grava
      // enquanto ainda não tiver a 1ª), então disparar em toda mensagem é
      // seguro. Best-effort: falha de rede aqui não pode quebrar o envio.
      if (!isPrivate) {
        logFirstAgentMessage(convId, options.getMyAgentId()).catch(() => { /* best-effort */ })
      }

      // Conversa sem responsável + agente respondeu → auto-atribui a ele.
      // Via comando n8n (token de serviço), não com o token pessoal do
      // agente, que pode não ter permissão de atribuição fora do seu
      // time/inbox no Chatwoot.
      if (!options.getAssigneeId()) {
        const myId = options.getMyAgentId()
        if (myId) {
          const assigned = await assignAgentViaSystem(convId, myId).catch(() => false)
          if (assigned) options.onAutoAssigned?.(convId, myId)
        }
      }

      // Agente respondeu conversa com etiqueta "atendimento fora de horario" → remove a etiqueta.
      const currentLabels = options.getLabels?.() ?? []
      const foraLabel = currentLabels.find(isForaDeHorarioLabel)
      if (foraLabel) {
        const nextLabels = currentLabels.filter(l => l !== foraLabel)
        const result = await setConversationLabels(convId, token, nextLabels).catch(() => null)
        if (result) options.onLabelsChanged?.(convId, result)
      }

      // Conversa pendente + agente respondeu → reabre automaticamente. Via
      // comando n8n (token de serviço), mesmo motivo do auto-atribuir acima.
      if (options.getStatus?.() === 'pending') {
        const reopened = await toggleConversationStatusViaSystem(convId, 'open').catch(() => false)
        if (reopened) options.onStatusChanged?.(convId, 'open')
      }
    } catch (e) {
      console.error('[chat] send:', e)
    } finally {
      options.onSent?.(convId)
      sending.value = false
      await nextTick()
      options.scrollBottom()
    }
  }

  return { sending, sendMsg }
}
